// Studio mastering engine (Web Audio + JS DSP): HPF -> corrective/tonal EQ -> reference match EQ -> 3-band LR4 multiband compression
// -> M/S (mono bass + width) -> analog saturation (4x oversampled) -> drive -> look-ahead brickwall limiter -> true-peak safety trim.
import { Mp3Encoder } from "@breezystack/lamejs";

export const dbToGain = (db) => Math.pow(10, db / 20);
export const gainToDb = (g) => (g > 0 ? 20 * Math.log10(g) : -Infinity);

export const GENRES = {
  techno: { label: "Techno", sub: "Cassa dritta, bassi solidi", low: 1.5, lowMid: -1.5, mid: 0.5, high: 1, width: 1.05, mono: 140, comp: 4, punch: 3, sat: 2.5, satType: "tape" },
  techhouse: { label: "Tech House", sub: "Groove e low-end corposo", low: 2, lowMid: -1.5, mid: 0.5, high: 1.5, width: 1.08, mono: 130, comp: 4.5, punch: 3.5, sat: 2.5, satType: "tape" },
  house: { label: "House", sub: "Caldo, aperto, ballabile", low: 1.5, lowMid: -1, mid: 0.5, high: 1.5, width: 1.1, mono: 120, comp: 3.5, punch: 2.5, sat: 2, satType: "tube" },
  deep: { label: "Deep House", sub: "Morbido, profondo, avvolgente", low: 1.5, lowMid: -0.5, mid: -0.5, high: 0.5, width: 1.12, mono: 110, comp: 3, punch: 2, sat: 3, satType: "tube" },
  melodic: { label: "Melodic Techno", sub: "Ampio, arioso, emozionale", low: 1, lowMid: -1, mid: 0, high: 2, width: 1.18, mono: 120, comp: 3.5, punch: 2, sat: 1.5, satType: "tape" },
  afro: { label: "Afro House", sub: "Percussioni vive, bassi rotondi", low: 2, lowMid: -1, mid: 1, high: 1.5, width: 1.1, mono: 110, comp: 3.5, punch: 3, sat: 2, satType: "tube" },
  minimal: { label: "Minimal", sub: "Pulito, preciso, dinamico", low: 1, lowMid: -1, mid: 0, high: 1, width: 1, mono: 130, comp: 3, punch: 2.5, sat: 1.5, satType: "tape" },
  dnb: { label: "Drum & Bass", sub: "Aggressivo, sub potenti", low: 2, lowMid: -2, mid: 1, high: 2, width: 1.1, mono: 150, comp: 5, punch: 4, sat: 3, satType: "tape" },
  natural: { label: "Naturale", sub: "Intervento minimo, massima dinamica", low: 0.5, lowMid: 0, mid: 0, high: 0.5, width: 1, mono: 100, comp: 1.5, punch: 1, sat: 0.5, satType: "tape" },
};
export const MODES = {
  streaming: { label: "Streaming", sub: "Spotify, Apple Music · -14 LUFS", target: -14, ceiling: -1 },
  club: { label: "Club / DJ", sub: "Beatport, pista · -8.5 LUFS", target: -8.5, ceiling: -0.8 },
};
export const makeSettings = (genre, mode) => {
  const g = GENRES[genre], m = MODES[mode];
  return { ...g, ...m, comp: mode === "club" ? Math.min(g.comp + 1.5, 10) : g.comp, eqMatch: null, drive: 0 };
};
export const MATCH_BANDS = [31, 63, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];

export const ensureStereo = (ctx, buf) => {
  if (buf.numberOfChannels >= 2) return buf;
  const out = ctx.createBuffer(2, buf.length, buf.sampleRate);
  out.copyToChannel(buf.getChannelData(0), 0);
  out.copyToChannel(buf.getChannelData(0), 1);
  return out;
};

const biquad = (ctx, type, freq, q = 0.7071, gain = 0) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q; f.gain.value = gain; return f; };
const series = (...nodes) => { nodes.reduce((a, b) => (a.connect(b), b)); return { first: nodes[0], last: nodes[nodes.length - 1] }; };
const comp = (ctx, attack, release, knee) => { const c = ctx.createDynamicsCompressor(); c.attack.value = attack; c.release.value = release; c.knee.value = knee; return c; };

const satCurve = (amount, type) => {
  const k = 1 + amount * 0.45, n = 4096, curve = new Float32Array(n), bias = type === "tube" ? 0.12 : 0;
  const norm = Math.tanh(k * (1 + bias)) - Math.tanh(k * bias);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; curve[i] = (Math.tanh(k * (x + bias)) - Math.tanh(k * bias)) / norm; }
  return curve;
};

export function buildChain(ctx, { live = false } = {}) {
  const input = ctx.createGain();
  const hp = series(biquad(ctx, "highpass", 28), biquad(ctx, "highpass", 28));
  const low = biquad(ctx, "lowshelf", 90), mud = biquad(ctx, "peaking", 280, 1), pres = biquad(ctx, "peaking", 3000, 0.8), air = biquad(ctx, "highshelf", 11000);
  const match = MATCH_BANDS.map((f) => biquad(ctx, "peaking", f, 1.4));
  const eq = series(input, hp.first); series(hp.last, low, mud, pres, air, ...match);
  void eq;
  const tail = match[match.length - 1];
  // 3-band Linkwitz-Riley (LR4) crossover at 120 Hz / 4 kHz
  const lowB = series(biquad(ctx, "lowpass", 120), biquad(ctx, "lowpass", 120));
  const midB = series(biquad(ctx, "highpass", 120), biquad(ctx, "highpass", 120), biquad(ctx, "lowpass", 4000), biquad(ctx, "lowpass", 4000));
  const highB = series(biquad(ctx, "highpass", 4000), biquad(ctx, "highpass", 4000));
  const cL = comp(ctx, 0.03, 0.12, 6), cM = comp(ctx, 0.012, 0.18, 6), cH = comp(ctx, 0.005, 0.1, 6);
  const bandSum = ctx.createGain();
  [[lowB, cL], [midB, cM], [highB, cH]].forEach(([b, c]) => { tail.connect(b.first); b.last.connect(c); c.connect(bandSum); });
  // Mid/Side: side high-passed (mono bass) and scaled (width)
  const split = ctx.createChannelSplitter(2), merge = ctx.createChannelMerger(2);
  bandSum.connect(split);
  const g = (v) => { const n = ctx.createGain(); n.gain.value = v; return n; };
  const mid = ctx.createGain(), side = ctx.createGain();
  split.connect(g(0.5), 0).connect(mid); split.connect(g(0.5), 1).connect(mid);
  split.connect(g(0.5), 0).connect(side); split.connect(g(-0.5), 1).connect(side);
  const sideHp1 = biquad(ctx, "highpass", 120), sideHp2 = biquad(ctx, "highpass", 120), width = ctx.createGain();
  side.connect(sideHp1); sideHp1.connect(sideHp2); sideHp2.connect(width);
  mid.connect(merge, 0, 0); mid.connect(merge, 0, 1);
  width.connect(merge, 0, 0); width.connect(g(-1)).connect(merge, 0, 1);
  // Saturation (parallel, 4x oversampled)
  const shaper = ctx.createWaveShaper(); shaper.oversample = "4x";
  const dry = ctx.createGain(), wet = ctx.createGain(), drive = ctx.createGain(), out = ctx.createGain();
  merge.connect(dry); merge.connect(shaper); shaper.connect(wet); dry.connect(drive); wet.connect(drive);
  let output = drive;
  if (live) { const lim = comp(ctx, 0.002, 0.06, 0); lim.ratio.value = 20; drive.connect(lim); lim.connect(out); output = out; } else { drive.connect(out); output = out; }
  const update = (s) => {
    low.gain.value = s.low; mud.gain.value = s.lowMid; pres.gain.value = s.mid; air.gain.value = s.high;
    match.forEach((f, i) => { f.gain.value = s.eqMatch?.[i] || 0; });
    cL.threshold.value = -12 - s.punch * 1.5; cL.ratio.value = 1.5 + s.punch * 0.35;
    cM.threshold.value = -14 - s.comp * 1.4; cM.ratio.value = 1.3 + s.comp * 0.25;
    cH.threshold.value = -18 - s.comp * 1.2; cH.ratio.value = 1.3 + s.comp * 0.2;
    sideHp1.frequency.value = s.mono; sideHp2.frequency.value = s.mono;
    width.gain.value = s.width;
    shaper.curve = satCurve(s.sat, s.satType);
    const mix = Math.min(s.sat / 10, 1) * 0.6;
    wet.gain.value = mix; dry.gain.value = 1 - mix;
    drive.gain.value = dbToGain(s.drive || 0);
  };
  return { input, output, update };
}

// Look-ahead brickwall limiter: sliding-window minimum of required gain + smoothed attack + exponential release.
export function limitBuffer(buf, ceilDb) {
  const n = buf.length, sr = buf.sampleRate, ceil = dbToGain(ceilDb - 0.3);
  const chans = Array.from({ length: buf.numberOfChannels }, (_, c) => buf.getChannelData(c));
  const la = Math.max(8, Math.round(sr * 0.0015)), W = la * 2 + 1, relK = Math.exp(-1 / (sr * 0.08));
  const req = new Float32Array(n);
  for (let i = 0; i < n; i++) { let p = 0; for (const d of chans) { const v = Math.abs(d[i]); if (v > p) p = v; } req[i] = p > ceil ? ceil / p : 1; }
  // Gil-Werman sliding minimum over [i, i+W-1]
  const pre = new Float32Array(n), suf = new Float32Array(n);
  for (let i = 0; i < n; i++) pre[i] = i % W === 0 ? req[i] : Math.min(pre[i - 1], req[i]);
  for (let i = n - 1; i >= 0; i--) suf[i] = i === n - 1 || (i + 1) % W === 0 ? req[i] : Math.min(suf[i + 1], req[i]);
  const held = new Float32Array(n);
  for (let i = 0; i < n; i++) { const j = Math.min(i + W - 1, n - 1); held[i] = Math.min(suf[i], pre[j]); }
  let sum = 0, env = 1, reduction = 1;
  for (let i = 0; i < n; i++) {
    sum += held[i]; if (i >= la) sum -= held[i - la];
    const sm = Math.min(sum / Math.min(i + 1, la), held[i]);
    env = sm < env ? sm : sm - (sm - env) * relK;
    if (env < reduction) reduction = env;
    for (const d of chans) d[i] *= env;
  }
  return gainToDb(reduction);
}

// True peak (4x oversampled, windowed-sinc), evaluated only around loud samples for speed.
const TP_TAPS = (() => {
  const taps = 8, phases = [0.25, 0.5, 0.75];
  return phases.map((ph) => { const k = []; for (let t = -taps + 1; t <= taps; t++) { const x = t - ph; const sinc = x === 0 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x); const w = 0.5 + 0.5 * Math.cos((Math.PI * x) / taps); k.push(sinc * w); } return k; });
})();
export function truePeak(buf) {
  let tp = 0;
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c), n = d.length;
    let sp = 0; for (let i = 0; i < n; i++) { const v = Math.abs(d[i]); if (v > sp) sp = v; }
    if (sp > tp) tp = sp;
    const thr = sp * 0.7;
    for (let i = 8; i < n - 8; i++) {
      if (Math.abs(d[i]) < thr && Math.abs(d[i + 1]) < thr) continue;
      for (const k of TP_TAPS) { let s = 0; for (let t = 0; t < 16; t++) s += d[i - 7 + t] * k[t]; const v = Math.abs(s); if (v > tp) tp = v; }
    }
  }
  return gainToDb(tp);
}

export const samplePeak = (buf) => {
  let p = 0;
  for (let c = 0; c < buf.numberOfChannels; c++) { const d = buf.getChannelData(c); for (let i = 0; i < d.length; i++) { const v = Math.abs(d[i]); if (v > p) p = v; } }
  return p;
};

async function kWeighted(buffer) {
  const off = new OfflineAudioContext(buffer.numberOfChannels, buffer.length, buffer.sampleRate);
  const src = off.createBufferSource(); src.buffer = buffer;
  const shelf = biquad(off, "highshelf", 1681, 0.7071, 4), hp = biquad(off, "highpass", 38, 0.5);
  src.connect(shelf); shelf.connect(hp); hp.connect(off.destination); src.start();
  return off.startRendering();
}
const blockLoudness = (k, blockSec, stepSec) => {
  const sr = k.sampleRate, block = Math.round(blockSec * sr), step = Math.round(stepSec * sr);
  const chans = Array.from({ length: k.numberOfChannels }, (_, c) => k.getChannelData(c));
  const out = [];
  for (let start = 0; start + block <= k.length; start += step) {
    let sum = 0;
    for (const d of chans) { let ms = 0; for (let i = start; i < start + block; i++) ms += d[i] * d[i]; sum += ms / block; }
    out.push(sum);
  }
  return out;
};
const loud = (z) => -0.691 + 10 * Math.log10(z);
const gated = (blocks, rel) => {
  const abs = blocks.filter((z) => loud(z) > -70);
  if (!abs.length) return [];
  const g = loud(abs.reduce((a, b) => a + b, 0) / abs.length) - rel;
  return abs.filter((z) => loud(z) > g);
};
export async function measureLufs(buffer) {
  const rel = gated(blockLoudness(await kWeighted(buffer), 0.4, 0.1), 10);
  return rel.length ? loud(rel.reduce((a, b) => a + b, 0) / rel.length) : -70;
}

// Full studio analysis: integrated LUFS, max short-term, loudness range (LRA), true peak, PLR, tonal spectrum.
export async function analyze(buffer) {
  const k = await kWeighted(buffer);
  const rel = gated(blockLoudness(k, 0.4, 0.1), 10);
  const lufs = rel.length ? loud(rel.reduce((a, b) => a + b, 0) / rel.length) : -70;
  const st = blockLoudness(k, 3, 1);
  const stMax = st.length ? Math.max(...st.map(loud)) : lufs;
  const stG = gated(st, 20).map(loud).sort((a, b) => a - b);
  const lra = stG.length > 2 ? stG[Math.floor(stG.length * 0.95) - 1] - stG[Math.floor(stG.length * 0.1)] : 0;
  const tp = truePeak(buffer);
  return { lufs, stMax, lra: Math.max(0, lra), truePeak: tp, plr: tp - lufs, bands: bandSpectrum(buffer) };
}

function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) { let bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit; if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; } }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len, wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let j = 0; j < len / 2; j++) {
        const a = i + j, b = a + len / 2, tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        const nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
      }
    }
  }
}
// Average energy per octave band (dB, relative to mean) from ~48 FFT frames across the track.
export function bandSpectrum(buf) {
  const N = 8192, sr = buf.sampleRate, L = buf.getChannelData(0), R = buf.getChannelData(buf.numberOfChannels > 1 ? 1 : 0);
  const frames = Math.min(48, Math.max(1, Math.floor(buf.length / N)));
  const acc = new Float64Array(MATCH_BANDS.length);
  const re = new Float64Array(N), im = new Float64Array(N);
  for (let f = 0; f < frames; f++) {
    const start = Math.floor(((buf.length - N) * (f + 0.5)) / frames);
    for (let i = 0; i < N; i++) { const w = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1)); re[i] = ((L[start + i] || 0) + (R[start + i] || 0)) * 0.5 * w; im[i] = 0; }
    fft(re, im);
    for (let b = 1; b < N / 2; b++) {
      const hz = (b * sr) / N, idx = Math.round(Math.log2(hz / 31.25));
      if (idx >= 0 && idx < MATCH_BANDS.length) acc[idx] += re[b] * re[b] + im[b] * im[b];
    }
  }
  const db = Array.from(acc, (v) => 10 * Math.log10(v + 1e-12));
  const mean = db.reduce((a, b) => a + b, 0) / db.length;
  return db.map((v) => v - mean);
}
// Target tonal balance per genre family (relative dB per octave band 31 Hz..16 kHz), used by the automatic tonal balance.
const CURVES = {
  club: [4, 5, 3, 1, 0, -1, -2, -3, -4, -7],
  groove: [3, 4.5, 3, 1, 0, -1, -1.5, -2.5, -3.5, -6.5],
  deep: [3, 4, 2.5, 1, 0, -0.5, -1.5, -2.5, -3.5, -6],
  clean: [2.5, 4, 2.5, 1, 0, -1, -2, -3, -4, -7],
};
const GENRE_CURVE = { techno: "club", techhouse: "club", dnb: "club", house: "groove", afro: "groove", deep: "deep", melodic: "deep", minimal: "clean", natural: "clean" };
export const autoBalanceEq = (bands, genre) => matchEq(bands, CURVES[GENRE_CURVE[genre] || "groove"], 0.5);

export const matchEq = (srcBands, refBands, strength = 0.7) => {
  const diff = refBands.map((r, i) => r - srcBands[i]);
  const mean = diff.reduce((a, b) => a + b, 0) / diff.length;
  return diff.map((d) => Math.max(-4, Math.min(4, (d - mean) * strength)));
};

async function renderChain(buffer, s) {
  const off = new OfflineAudioContext(2, buffer.length, buffer.sampleRate);
  const src = off.createBufferSource(); src.buffer = buffer;
  const chain = buildChain(off); chain.update(s);
  src.connect(chain.input); chain.output.connect(off.destination); src.start();
  return off.startRendering();
}
export async function renderMaster(buffer, s) {
  const out = await renderChain(buffer, s);
  const gr = limitBuffer(out, s.ceiling);
  const tp = truePeak(out);
  if (tp > s.ceiling) { const k = dbToGain(s.ceiling - tp); for (let c = 0; c < out.numberOfChannels; c++) { const d = out.getChannelData(c); for (let i = 0; i < d.length; i++) d[i] *= k; } }
  return { out, gr };
}

// Renders, measures and corrects drive until the master hits the target loudness (max 4 passes).
export async function autoMaster(buffer, s, onStep) {
  let drive = 0, res = null, lufs = null;
  for (let pass = 1; pass <= 5; pass++) {
    onStep?.(`Elaborazione da studio: passaggio ${pass} di 5…`);
    res = await renderMaster(buffer, { ...s, drive });
    lufs = await measureLufs(res.out);
    if (Math.abs(lufs - s.target) < 0.4) break;
    drive = Math.min(Math.max(drive + (s.target - lufs) * (pass > 2 ? 0.85 : 1), -30), 24);
  }
  onStep?.("Analisi finale del master…");
  const stats = await analyze(res.out);
  return { buffer: res.out, ...stats, peakDb: stats.truePeak, drive, gainReduction: res.gr };
}

export const sliceBuffer = (buf, startSec, durSec) => {
  const start = Math.max(0, Math.floor(startSec * buf.sampleRate)), len = Math.min(buf.length - start, Math.floor(durSec * buf.sampleRate));
  const out = new AudioBuffer({ numberOfChannels: buf.numberOfChannels, length: len, sampleRate: buf.sampleRate });
  for (let c = 0; c < buf.numberOfChannels; c++) out.copyToChannel(buf.getChannelData(c).subarray(start, start + len), c);
  return out;
};

export async function resample(buf, sr) {
  if (buf.sampleRate === sr) return buf;
  const off = new OfflineAudioContext(buf.numberOfChannels, Math.ceil((buf.length * sr) / buf.sampleRate), sr);
  const src = off.createBufferSource(); src.buffer = buf; src.connect(off.destination); src.start();
  return off.startRendering();
}

function wavHeader(view, ch, sr, bits, len) {
  const str = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  const bytes = bits / 8;
  str(0, "RIFF"); view.setUint32(4, 36 + len * ch * bytes, true); str(8, "WAVE"); str(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, ch, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * ch * bytes, true); view.setUint16(32, ch * bytes, true);
  view.setUint16(34, bits, true); str(36, "data"); view.setUint32(40, len * ch * bytes, true);
}
export function encodeWav24(buf) {
  const ch = buf.numberOfChannels, len = buf.length;
  const data = new DataView(new ArrayBuffer(44 + len * ch * 3));
  wavHeader(data, ch, buf.sampleRate, 24, len);
  const chans = Array.from({ length: ch }, (_, c) => buf.getChannelData(c));
  let o = 44;
  for (let i = 0; i < len; i++) for (let c = 0; c < ch; c++) {
    const v = Math.max(-1, Math.min(1, chans[c][i]));
    const n = Math.round(v < 0 ? v * 0x800000 : v * 0x7fffff);
    data.setUint8(o, n & 0xff); data.setUint8(o + 1, (n >> 8) & 0xff); data.setUint8(o + 2, (n >> 16) & 0xff); o += 3;
  }
  return new Blob([data], { type: "audio/wav" });
}
// 16-bit with TPDF dither (CD / distributor standard).
const toInt16 = (d) => { const out = new Int16Array(d.length); for (let i = 0; i < d.length; i++) { const v = d[i] * 32767 + (Math.random() - Math.random()); out[i] = Math.max(-32768, Math.min(32767, Math.round(v))); } return out; };
export function encodeWav16(buf) {
  const ch = buf.numberOfChannels, len = buf.length;
  const data = new DataView(new ArrayBuffer(44 + len * ch * 2));
  wavHeader(data, ch, buf.sampleRate, 16, len);
  const ints = Array.from({ length: ch }, (_, c) => toInt16(buf.getChannelData(c)));
  let o = 44;
  for (let i = 0; i < len; i++) for (let c = 0; c < ch; c++) { data.setInt16(o, ints[c][i], true); o += 2; }
  return new Blob([data], { type: "audio/wav" });
}
export async function encodeMp3(buf, onProgress) {
  const enc = new Mp3Encoder(2, buf.sampleRate, 320);
  const L = toInt16(buf.getChannelData(0)), R = toInt16(buf.getChannelData(1));
  const parts = [], step = 1152 * 40;
  for (let i = 0; i < L.length; i += step) {
    const b = enc.encodeBuffer(L.subarray(i, i + step), R.subarray(i, i + step));
    if (b.length) parts.push(new Uint8Array(b));
    if ((i / step) % 20 === 0) { onProgress?.(Math.round((i / L.length) * 100)); await new Promise((r) => setTimeout(r, 0)); }
  }
  const end = enc.flush(); if (end.length) parts.push(new Uint8Array(end));
  return new Blob(parts, { type: "audio/mpeg" });
}
